import { createFileRoute, isNotFound } from '@tanstack/react-router';
import { buttonVariants } from '@worldcoo/ui/components/button';
import * as z from 'zod';
import { RadioGroup, RadioGroupItem } from '@worldcoo/ui/components/radio-group';
import { useTranslation } from '@/lib/i18n';
import { sanitizeHTML } from '@worldcoo/ui/lib/sanitize-html';
import { cn } from '@worldcoo/ui/lib/utils';
import CurrencyInput from 'react-currency-input-field';
import { Paragraph, Title, useAppForm } from '@worldcoo/ui/components/form';
import { Label } from '@worldcoo/ui/components/label';
import { Animate } from '@worldcoo/ui/custom/animate';
import { usePersistedStore } from '@/lib/app-store';
import { usePortalCampaignQuery } from '@/lib/queries';
import { FieldInfo } from '@worldcoo/ui/components/form/shared';

export const Route = createFileRoute('/$portalId/$campaignId/donate/')({
	component: Component,
});

function Component() {
	const { portalId, campaignId } = Route.useParams();
	const navigate = Route.useNavigate();
	const { i18n } = useTranslation();
	const amount = usePersistedStore((state) => state.amount);
	const setAmount = usePersistedStore((state) => state.setAmount);
	const form = useAppForm({
		validators: {
			onChange: z.object({
				amount: z
					.string()
					.min(1, i18n.errors.amount)
					.check((ctx) => {
						if (ctx.issues.length === 0 && Number(ctx.value) <= 0) {
							ctx.issues.push({ code: 'custom', message: i18n.errors.amount, input: ctx.value });
						}
					}),
			}),
		},
		defaultValues: { amount: amount?.toString() ?? '10' },
		onSubmit: ({ value }) => {
			setAmount(value.amount ? Number(value.amount) : undefined);
			navigate({ to: `/$portalId/$campaignId/donate/details`, params: { portalId, campaignId } });
		},
	});

	const possibleAmounts = ['5', '10', '25', '50'];

	return (
		<Animate>
			<form
				onSubmit={(event) => {
					event.preventDefault();
					form.handleSubmit();
				}}
				className="flex h-full grow flex-col gap-6"
			>
				<div className="flex flex-col gap-3">
					<Title size="lg" className="text-center">
						{i18n.donate.title}
					</Title>
					<Paragraph className="text-center" as="div">
						<CampaignText />
					</Paragraph>
				</div>

				<form.AppField
					name="amount"
					children={(field) => (
						<>
							<RadioGroup
								onValueChange={field.handleChange}
								value={field.state.value}
								className="grid grid-cols-1 gap-4 lg:grid-cols-2"
							>
								{possibleAmounts.map((amount) => (
									<div key={amount} className="flex">
										<RadioGroupItem value={amount} id={`amount-${amount}`} className="peer invisible sr-only" />
										<Label
											htmlFor={`amount-${amount}`}
											className={cn(
												buttonVariants({ variant: field.state.value === amount ? 'default' : 'outlineAlt' }),
												'h-[58px] w-full cursor-pointer text-[20px]',
											)}
										>
											{amount}€
										</Label>
									</div>
								))}
							</RadioGroup>

							<div className="grow">
								<CurrencyInput
									className={cn(
										buttonVariants({
											variant:
												field.state.value === '' || possibleAmounts.includes(field.state.value)
													? 'outlineAlt'
													: 'default',
										}),
										'placeholder:text-foreground h-[58px] w-full text-center text-[20px] focus:placeholder:text-transparent',
									)}
									allowNegativeValue={false}
									placeholder={i18n.placeholders.amount}
									decimalsLimit={2}
									decimalSeparator={'.'}
									groupSeparator={','}
									suffix={'€'}
									value={possibleAmounts.includes(field.state.value) ? undefined : field.state.value}
									onValueChange={(value) => field.setValue(value ?? '')}
								/>
								<FieldInfo fieldState={field.state} />
							</div>
						</>
					)}
				/>

				<form.AppForm>
					<form.SubmitButton size="2xl" className="mx-auto mt-6 w-full min-w-fit lg:w-60" label={i18n.actions.next} />
				</form.AppForm>
			</form>
		</Animate>
	);
}

function CampaignText() {
	const { campaignId } = Route.useParams();
	const campaign = usePortalCampaignQuery(campaignId);

	if (isNotFound(campaign)) return null;

	return (
		<>
			{campaign.data.texts.objectives && (
				<div dangerouslySetInnerHTML={{ __html: sanitizeHTML(campaign.data.texts.objectives) }} />
			)}
		</>
	);
}
